import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { CommonButton, CommonButtonV1, CommonModal } from 'gemlux-common-components';
import { differenceInDays } from 'date-fns';
import { useMemo } from 'react';
import { PlanType } from '../../../../utils/enums/plan-type.enum';
import { theme } from '../../../../theme/theme';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { authActions } from '../../../../redux/auth/authSlice';
import TickSvg from '../../../../assets/img/tick-white.svg';
import { IProduct } from '../../../../redux/subscription/subscriptionSlice';
import { RootState } from '../../../../redux/store';
import BasicPlanSvg from '../../../../assets/img/tag.svg';
import PremiumPlanSvg from '../../../../assets/img/crown.svg';
import CoporatePlanSvg from '../../../../assets/img/briefcase.svg';
import { UpdateType } from '../../../../utils/enums/subscription-update-type.enum';
import { organizationActions } from '../../../../redux/organization/organizationSlice';

type IPlanProps = {
  type: PlanType;
  setActiveStep?: (_activeStep: number) => void;
  product: IProduct;
  isUpdatable?: boolean;
  viewOnly?: boolean;
  setShowUpdatePlanModal?: (_value: boolean) => void;
  setUpdatedToPlan?: (_updatedPlan: PlanType) => void;
  setBaseValue?: (_baseValue: number | null) => void;
  setUpdatedType?: (_updatedType: UpdateType) => void;
  setSubscriptionId?: (_subscriptionId: string | undefined) => void;
  onDowngradeHover?: () => void;
};

type UpdateSubscriptionPlanModalProps = {
  updatedPlan: PlanType;
  updatedType: UpdateType;
  subscriptionId: string | undefined;
  baseSubscriptionAmount: number | null;
};
const excessCapTextStyle = {
  color: 'var(--Texts-text-base, #14181F)',
  fontFamily: 'Inter',
  fontSize: '11px',
  fontStyle: 'italic',
  fontWeight: 400,
  lineHeight: 'normal',
};

function Plan({
  type,
  setActiveStep,
  product,
  isUpdatable,
  viewOnly,
  setShowUpdatePlanModal,
  setUpdatedToPlan,
  setBaseValue,
  setUpdatedType,
  setSubscriptionId,
  onDowngradeHover,
}: IPlanProps) {
  const dispatch = useAppDispatch();
  const { signUpData = { plan: '', roles: [], questionOneAnswer: '', questionTwoAnswer: '' } } =
    useAppSelector((state: RootState) => state.auth) || {};
  const { organizationSubscription } = useAppSelector((state) => state.organization);
  const parseOrder = (order: string | undefined) => parseInt(order ?? '0', 10);
  const isUpgrade = useMemo(
    () => parseOrder(product.order) > parseOrder(organizationSubscription?.activeSubscription?.order),
    [product.order, organizationSubscription],
  );
  const isDowngradeDisabled =
    differenceInDays(new Date(), new Date(organizationSubscription?.activatedAt ?? '')) <= 31 &&
    parseInt(product.order ?? '0', 10) < parseInt(organizationSubscription?.activeSubscription?.order ?? '0', 10);
  const handleSelectPlan = () => {
    if (setActiveStep) {
      dispatch(authActions.setSignUpData({ lastActiveStep: 4, plan: product.subscriptionId }));
      setActiveStep(4);
    }
  };

  const handleUpdateSubscriptionModal = ({
    updatedPlan,
    updatedType,
    subscriptionId,
  }: UpdateSubscriptionPlanModalProps) => {
    if (setShowUpdatePlanModal && setUpdatedToPlan && setBaseValue && setUpdatedType && setSubscriptionId) {
      setShowUpdatePlanModal(true);
      setUpdatedToPlan(updatedPlan);
      setBaseValue(product.baseValue);
      setUpdatedType(updatedType);
      setSubscriptionId(subscriptionId);
      dispatch(organizationActions.resetSubscriptionUpdateSuccess());
    }
  };

  const getPlanIcon = (planType: PlanType): string | undefined => {
    switch (planType) {
      case PlanType.BASIC:
        return BasicPlanSvg;
      case PlanType.PREMIUM:
        return PremiumPlanSvg;
      case PlanType.CORPORATE:
        return CoporatePlanSvg;
      default:
        break;
    }
  };

  const renderPlanStatus = () => {
    if (
      isUpdatable &&
      organizationSubscription?.activeSubscription?.subscriptionId === product.subscriptionId &&
      !organizationSubscription?.requestedSubscriptionId
    ) {
      return (
        <Grid
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            padding: '8px 24px',
            backgroundColor: '#1C2EB0',
            borderRadius: '4px 4px 0px 0px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
          }}
        >
          <Typography variant="subtitle1" color="secondary.light">
            You’re on this plan
          </Typography>
        </Grid>
      );
    }

    if (isUpdatable && organizationSubscription?.requestedSubscriptionId && type === PlanType.CORPORATE) {
      return (
        <Grid
          p="8px 24px"
          bgcolor={theme.palette.shades.warningBrightMain}
          borderRadius="4px 4px 0px 0px"
          width="max-content"
          height={32}
          display="flex"
          alignItems="center"
          justifyContent="flex-start"
          position="absolute"
        >
          <Typography variant="subtitle1" color="#FFFFFF">
            Pending Processing
          </Typography>
        </Grid>
      );
    }

    return null;
  };

  const getBorderStyle = (): { borderStyle: string; borderColor: string } => ({
    borderStyle: 'none',
    borderColor: '',
  });
  const { borderStyle, borderColor } = getBorderStyle();

  const buttonTextColor = (downgradeDisabled: boolean, upgradable: boolean) => {
    if (upgradable) {
      return theme.palette.white.main; // Text color for "Upgrade Now!"
    }
    if (downgradeDisabled) {
      return theme.palette.action.disabledBackground; // Text color when downgrade is disabled
    }
    return theme.palette.shades.buttonOutline; // Default text color
  };

  const getButtonStyle = (downgradeDisabled: boolean, upgradable: boolean) => ({
    border: `1px solid ${downgradeDisabled ? theme.palette.action.disabledBackground : '#3348FF'}`,
    backgroundColor: upgradable ? '#3348FF' : 'transparent',
    color: buttonTextColor(downgradeDisabled, upgradable),
    pointerEvents: downgradeDisabled ? ('none' as const) : ('auto' as const),
  });

  const backgroundColors: Record<PlanType, string> = {
    [PlanType.BASIC]: '#E0EDFF',
    [PlanType.PREMIUM]: '#FFF6D1',
    [PlanType.CORPORATE]: '#DBFFE1',
  };

  const getButtonText = (upgradable: boolean) => (upgradable ? 'Upgrade Plan!' : 'Downgrade');

  return (
    <Grid display="flex" flexDirection="column">
      <Grid
        container
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        style={{ marginBottom: 0, position: 'relative' }}
      >
        {type === PlanType.BASIC && !isUpdatable ? (
          <Grid
            p="8px 24px"
            bgcolor="#1C2EB0"
            borderRadius="4px 4px 0px 0px"
            width="max-content"
            height={32}
            alignItems="center"
            justifyContent="center"
          >
            <Typography variant="subtitle1" color="secondary.light">
              3 Months FREE TRIAL
            </Typography>
          </Grid>
        ) : (
          <Box height={32} />
        )}

        {renderPlanStatus()}
      </Grid>

      <Grid
        display="flex"
        flexDirection="row"
        gap={1.5}
        alignItems="flex-start"
        pb={4}
        bgcolor={backgroundColors[type] || '#FFFFFF'}
        sx={{
          border: '1px solid #DCE0E5',
          borderRadius: '12px 12px 0px 0px',
          padding: '24px 20px 16px 20px',
          borderBottom: 'none',
        }}
        marginTop="none"
      >
        <Box component="img" src={getPlanIcon(type)} width={36} />
        <Grid display="flex" flexDirection="column" gap={0.5}>
          <Typography variant="textBaseLg500" color="black.primary">
            {type}
          </Typography>
          <Typography variant="textBase500" color="#6F7C8E">
            {type === PlanType.CORPORATE ? 'Custom Pricing' : `USD ${product.baseValue}/mth`}
          </Typography>
        </Grid>
      </Grid>
      <Grid
        padding="24px 20px 16px 20px"
        display="flex"
        flexDirection="column"
        alignItems="left"
        bgcolor="#FFFFFF !important"
        gap={7}
        border={borderStyle}
        borderColor={borderColor}
        borderTop="none"
        sx={{
          border: '1px solid #DCE0E5',
        }}
      >
        <Grid display="flex" flexDirection="column" gap={1.5}>
          <Typography height={15} />
          <Typography variant="textSM" height={18} color="black.primary">
            Max {product.metadata?.user_limit} Users
          </Typography>
          <div style={{ height: '0.5px', backgroundColor: '#DCE0E5', width: '100%' }} />
          <Grid display="flex" flexDirection="column" gap={1.5}>
            <Typography height={37} />
            <Typography variant="textSM" height={18} color="black.primary">
              {type === PlanType.CORPORATE
                ? 'Unlimited'
                : `Max ${product.metadata?.image_storage_own_device} gemstones`}
            </Typography>
            <div style={{ height: '0.5px', backgroundColor: '#DCE0E5', width: '100%' }} />
            <Typography variant="textSM" height={17.5} color="black.primary">
              {type === PlanType.CORPORATE ? (
                'Included, no extra charge'
              ) : (
                <>
                  {`USD ${product.metadata?.image_capturing}/gemstone `}
                  <Typography component="span" style={excessCapTextStyle}>
                    (in excess of cap)
                  </Typography>
                </>
              )}
            </Typography>
            <div style={{ height: '0.5px', backgroundColor: '#DCE0E5', width: '100%' }} />
            <Typography variant="textSM" height={16} color="black.primary">
              {type === PlanType.CORPORATE
                ? 'Unlimited'
                : `Max ${product.metadata?.cloud_storage_krista_kroma} gemstones`}
            </Typography>
            <Typography height={3} />
          </Grid>
          <div style={{ height: '0.5px', backgroundColor: '#DCE0E5', width: '100%' }} />
          <Grid display="flex" flexDirection="column" gap={1.5}>
            <Typography height={28} />
            <Typography variant="textSM" height={13} color="black.primary">
              {type === PlanType.CORPORATE ? (
                'Included, no extra charge'
              ) : (
                <>
                  {`USD ${product.metadata?.capture_video}/gemstone `}
                  <Typography component="span" style={excessCapTextStyle}>
                    (in excess of cap)
                  </Typography>
                </>
              )}
            </Typography>
            <div style={{ height: '0.5px', backgroundColor: '#DCE0E5', width: '100%' }} />
            <Typography variant="textSM" height={5} color="black.primary">
              {type === PlanType.CORPORATE ? 'Unlimited' : `Max ${product.metadata?.video_storage} gemstones`}
            </Typography>
            <Typography height={1} />
          </Grid>
          <div style={{ height: '0.5px', backgroundColor: '#DCE0E5', width: '100%' }} />
          <Grid display="flex" flexDirection="column" gap={1.5}>
            <Typography height={28} />
            <Typography variant="textSM" height={13} color="black.primary">
              {type === PlanType.CORPORATE ? (
                'Included, no extra charge'
              ) : (
                <>
                  {`USD ${product.metadata?.gem_passport_production}/gemstone `}
                  <Typography component="span" style={excessCapTextStyle}>
                    (in excess of cap)
                  </Typography>
                </>
              )}
            </Typography>
            <div style={{ height: '0.5px', backgroundColor: '#DCE0E5', width: '100%' }} />{' '}
            <Typography variant="textSM" height={5} color="black.primary">
              {type === PlanType.CORPORATE
                ? 'Unlimited'
                : `Max ${product.metadata?.gem_passport_cloud_storage} gemstones`}
            </Typography>
            <Typography height={1} />
          </Grid>
          <div style={{ height: '0.5px', backgroundColor: '#DCE0E5', width: '100%' }} />
          <Grid display="flex" flexDirection="column" gap={1.5}>
            <Typography height={28} />
            <Typography variant="textSM" height={13} color="black.primary">
              {type === PlanType.CORPORATE ? (
                'Included, no extra charge'
              ) : (
                <>
                  {`USD ${product.metadata?.gem_passport_Lite_production}/gemstone `}
                  <Typography component="span" style={excessCapTextStyle}>
                    (in excess of cap)
                  </Typography>
                </>
              )}
            </Typography>
            <div style={{ height: '0.5px', backgroundColor: '#DCE0E5', width: '100%' }} />{' '}
            <Typography variant="textSM" height={18} color="black.primary">
              {type === PlanType.CORPORATE
                ? 'Unlimited'
                : `Max ${product.metadata?.gem_passport_lite_cloud_storage} gemstones`}
            </Typography>
            {/* <Typography height={4} /> */}
          </Grid>
          <div style={{ height: '0.5px', backgroundColor: '#DCE0E5', width: '100%' }} />
          <Typography height={10} />
          <Typography variant="textSM" height={18} color="black.primary">
            {product.metadata?.verify_gem_passport}
          </Typography>
        </Grid>
        {!isUpdatable && !viewOnly && (
          <CommonButtonV1
            variant="contained"
            onClick={handleSelectPlan}
            startIcon={signUpData.plan === product.subscriptionId && <Box component="img" src={TickSvg} alt="tick" />}
            height="46px"
            backgroundColor="#3348FF"
          >
            Select Plan
          </CommonButtonV1>
        )}
        {isUpdatable &&
          organizationSubscription?.activeSubscription?.subscriptionId !== product.subscriptionId &&
          !organizationSubscription?.requestedSubscriptionId && (
            <Box
              position="relative"
              display="inline-block"
              onMouseEnter={() => {
                if (isDowngradeDisabled) {
                  if (onDowngradeHover) {
                    onDowngradeHover();
                  }
                }
              }}
              sx={{ cursor: isDowngradeDisabled ? 'pointer' : 'default' }}
            >
              <CommonButtonV1
                onClick={() =>
                  handleUpdateSubscriptionModal({
                    updatedPlan: product.name,
                    updatedType: isUpgrade ? UpdateType.UPGRADED : UpdateType.DOWNGRADED,
                    subscriptionId: product?.subscriptionId,
                    baseSubscriptionAmount: product?.baseValue,
                  })
                }
                style={getButtonStyle(isDowngradeDisabled, isUpgrade)}
                disabled={isDowngradeDisabled}
                startIcon={
                  signUpData.plan === product.subscriptionId && <Box component="img" src={TickSvg} alt="tick" />
                }
                height="46px"
                fullWidth
              >
                {getButtonText(isUpgrade)}
              </CommonButtonV1>
              {isDowngradeDisabled && (
                <Box
                  position="absolute"
                  top={0}
                  left={0}
                  width="100%"
                  height="100%"
                  sx={{ backgroundColor: 'transparent' }}
                />
              )}
            </Box>
          )}
        {isUpdatable &&
          organizationSubscription?.requestedSubscriptionId !== product.subscriptionId &&
          organizationSubscription?.requestedSubscriptionId && (
            <CommonButtonV1
              style={getButtonStyle(true, false)}
              disabled
              startIcon={signUpData.plan === product.subscriptionId && <Box component="img" src={TickSvg} alt="tick" />}
              height="46px"
            >
              Downgrade
            </CommonButtonV1>
          )}
      </Grid>
    </Grid>
  );
}

export default Plan;
